import type {SidebarLink} from '@github-ui/saved-views'
import {useAnalytics} from '@github-ui/use-analytics'
import {TriangleDownIcon} from '@primer/octicons-react'
import {Button, NavList, Truncate} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useRef, useState} from 'react'

import {getHandlerNavItemClick} from '../utils/sidebar-link'
import styles from './ListSidebar.module.css'
import {SidebarLinkIcon} from './SidebarLinkIcon'

interface ListSidebarProps {
  links: SidebarLink[]
  onSelectLink: (linkItem: SidebarLink) => void
  selectedLink: string
}

export function ListSidebar({links, selectedLink, onSelectLink}: ListSidebarProps) {
  const {sendAnalyticsEvent} = useAnalytics()

  return (
    <NavList>
      {links.map(linkItem => (
        <NavList.Item
          aria-current={selectedLink === linkItem.id && 'page'}
          key={linkItem.id}
          href={`?q=${linkItem.query}`}
          onClick={getHandlerNavItemClick({onSelectLink, sendAnalyticsEvent, linkItem})}
        >
          <NavList.LeadingVisual>
            <SidebarLinkIcon name={linkItem.icon} color={linkItem.color} />
          </NavList.LeadingVisual>
          <Truncate title={linkItem.name} maxWidth="100%">
            {linkItem.name}
          </Truncate>
        </NavList.Item>
      ))}
    </NavList>
  )
}

interface ListSidebarCollapsedButtonProps extends ListSidebarProps {
  onCreateView?: () => void
  showCreateShortcuts?: boolean
}

export function ListSidebarCollapsedButton({
  onSelectLink,
  onCreateView,
  showCreateShortcuts = false,
  ...props
}: ListSidebarCollapsedButtonProps) {
  const [isDialogOpen, showDialog] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const selectedLinkItem = props.links.find(link => link.id === props.selectedLink)
  const listName = selectedLinkItem?.name || 'Search'

  return (
    <>
      <Button
        ref={buttonRef}
        variant="invisible"
        onClick={() => showDialog(true)}
        trailingVisual={TriangleDownIcon}
        aria-describedby="types-collapsed-button-description"
        className={styles.sidebarToggleButton}
      >
        <h1 className={styles.sidebarHeading}>
          <Truncate title={listName} maxWidth="100%">
            {listName}
          </Truncate>
        </h1>
      </Button>
      <span className="sr-only" id="types-collapsed-button-description" aria-live="polite">
        Change the repository type filter
      </span>
      {isDialogOpen && (
        <Dialog
          className={styles.dialog}
          onClose={() => showDialog(false)}
          title="Views"
          width="small"
          returnFocusRef={buttonRef}
          footerButtons={
            showCreateShortcuts && onCreateView
              ? [
                  {buttonType: 'default', content: 'Close', onClick: () => showDialog(false)},
                  {
                    buttonType: 'primary',
                    content: 'New view',
                    onClick: onCreateView,
                  },
                ]
              : []
          }
        >
          <ListSidebar
            onSelectLink={linkItem => {
              onSelectLink(linkItem)
              showDialog(false)
            }}
            {...props}
          />
        </Dialog>
      )}
    </>
  )
}
